import { CalendarProps } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import { Dispatch } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFiltersURLSearchParams } from '@features/campaigns/list/components/Filters/hooks/useFiltersURLSearchParams.ts';
import {
  Actions,
  CampaignListReducerAction,
  CampaignListReducerState,
} from '@features/campaigns/list/types/campaignListReducer.ts';
import {
  allCampaignCountries,
  allCampaignFormats,
  allCampaignStatuses,
  allCampaignTypes,
  CampaignCountryType,
  CampaignFormat,
  CampaignStatus,
  CampaignType,
} from '@features/campaigns/shared/types/campaign.ts';

interface UseCampaignListFiltersParams {
  state: CampaignListReducerState;
  dispatch: Dispatch<CampaignListReducerAction>;
}

export function useCampaignListFilters({ state, dispatch }: UseCampaignListFiltersParams) {
  const [searchParams, setSearchParams] = useSearchParams();

  useFiltersURLSearchParams({ state, searchParams, setSearchParams, dispatch });

  const filterMinDate = state?.filters.startDate ? new Date(state?.filters.startDate) : undefined;
  const filterMaxDate = state?.filters.endDate ? new Date(state?.filters.endDate) : undefined;

  const isMinimumOneFormatChecked = state.filters?.format && state.filters.format.length > 0;
  const campaignListState = state as CampaignListReducerState;
  const firstPageState = { first: 0, page: 0 };

  const handleOnSelectAllFormatCheckboxChange = (shouldCheckAllCheckboxes: boolean) => {
    const updatedFormatState = shouldCheckAllCheckboxes ? allCampaignFormats : [];

    dispatch({
      type: Actions.CHANGE_FORMAT_FILTER,
      payload: { ...firstPageState, filters: { format: updatedFormatState } },
    });
  };

  const handleOnFormatMultiSelectChange = ({ value }: { value: CampaignFormat[] }) => {
    dispatch({ type: Actions.CHANGE_FORMAT_FILTER, payload: { ...firstPageState, filters: { format: value } } });
  };

  const handleOnSelectAllStatusCheckboxClick = (shouldCheckAllCheckboxes: boolean) => {
    const updatedStatusState = shouldCheckAllCheckboxes ? allCampaignStatuses : [];

    dispatch({
      type: Actions.CHANGE_STATUS_FILTER,
      payload: { ...firstPageState, filters: { status: updatedStatusState } },
    });
  };

  const handleOnStatusMultiSelectChange = ({ value }: { value: CampaignStatus[] }) => {
    dispatch({
      type: Actions.CHANGE_STATUS_FILTER,
      payload: { ...firstPageState, filters: { status: value } },
    });
  };

  const handleOnSelectAllCampaignTypeCheckboxClick = (shouldCheckAllCheckboxes: boolean) => {
    const updatedCampaignTypeState = shouldCheckAllCheckboxes ? allCampaignTypes : [];

    dispatch({
      type: Actions.CHANGE_TYPE_FILTER,
      payload: { ...firstPageState, filters: { type: updatedCampaignTypeState } },
    });
  };

  const handleOnCampaignTypeMultiSelectChange = ({ value }: { value: CampaignType[] }) => {
    dispatch({
      type: Actions.CHANGE_TYPE_FILTER,
      payload: { ...firstPageState, filters: { type: value } },
    });
  };

  const handleOnSelectAllCampaignCountryCheckboxClick = (shouldCheckAllCheckboxes: boolean) => {
    const updatedCampaignCountryState = shouldCheckAllCheckboxes ? allCampaignCountries : [];

    dispatch({
      type: Actions.CHANGE_COUNTRY_FILTER,
      payload: { ...firstPageState, filters: { country: updatedCampaignCountryState } },
    });
  };

  const handleOnCampaignCountryMultiSelectChange = ({ value }: { value: CampaignCountryType[] }) => {
    dispatch({
      type: Actions.CHANGE_COUNTRY_FILTER,
      payload: { ...firstPageState, filters: { country: value } },
    });
  };

  const handleOnStartDateCalendarChange = ({ value }: CalendarProps) => {
    const startDate: Nullable<Date> = Array.isArray(value) ? value[0] : value;

    dispatch({
      type: Actions.CHANGE_START_DATE_FILTER,
      payload: { ...firstPageState, filters: { startDate } },
    });
  };

  const handleOnEndDateCalendarChange = ({ value }: CalendarProps) => {
    const endDate: Nullable<Date> = Array.isArray(value) ? value[0] : value;

    dispatch({ type: Actions.CHANGE_END_DATE_FILTER, payload: { ...firstPageState, filters: { endDate } } });
  };

  const handleOnAdvertiserMultiSelectChange = ({
    value,
    advertiserLabels,
  }: {
    value: string[];
    advertiserLabels?: Record<string, string>;
  }) => {
    dispatch({
      type: Actions.CHANGE_ADVERTISER_FILTER,
      payload: {
        ...firstPageState,
        filters: {
          advertiser: value.length ? value : undefined,
          advertiserLabels: value.length
            ? {
                ...campaignListState.filters?.advertiserLabels,
                ...advertiserLabels,
              }
            : undefined,
        },
      },
    });
  };

  const handleOnClearAllButtonClick = () => {
    dispatch({ type: Actions.CLEAR_FILTERS });
  };

  return {
    campaignListState,
    filterMinDate,
    filterMaxDate,
    isMinimumOneFormatChecked,
    handleOnSelectAllFormatCheckboxChange,
    handleOnFormatMultiSelectChange,
    handleOnSelectAllStatusCheckboxClick,
    handleOnStatusMultiSelectChange,
    handleOnSelectAllCampaignTypeCheckboxClick,
    handleOnCampaignTypeMultiSelectChange,
    handleOnSelectAllCampaignCountryCheckboxClick,
    handleOnCampaignCountryMultiSelectChange,
    handleOnAdvertiserMultiSelectChange,
    handleOnStartDateCalendarChange,
    handleOnEndDateCalendarChange,
    handleOnClearAllButtonClick,
  };
}
